import axios from 'axios';
import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import { observer, inject } from 'mobx-react';
import Image from 'next/image';
import Header from '@/components/header/Header';
import Footer from '@/components/footer/Footer';
import { getProductsData } from '@/utils/woocommerce';
import { getOnSaleProductsKey } from '@/utils/fetchers';

const DynamicProductsPage = dynamic(() => import('@/components/products/ProductsPage.js'), {
  loading: () => <div>Loading products...</div>,
});

const OnSalePage = inject('studioStore', 'wooCommerceStore')(observer(({
  initialProducts,
  initialCrossSellProducts,
  studioStore,
}) => {
  const paddingClass = `products-background ${studioStore.on_sale_page?.hero_image?.url ? "pt-[5rem] md:pt-36 pb-18" : "py-36"}`;

  return (
    <>
      <Header title={studioStore.on_sale_page.title} />
      <main className={paddingClass}>
        {studioStore.on_sale_page?.hero_image?.url && (
          <div className="relative w-full h-[10rem] md:h-[20rem] lg:h-[18.75rem] overflow-hidden">
            <Image src={studioStore.on_sale_page.hero_image.url} alt={studioStore.on_sale_page.hero_image.alt} style={{ objectFit: "cover" }} fill />
          </div>
        )}
        <div className="2xl:container mx-auto min-h-screen md:py-8">
          <div className="w-full px-4 2xl:px-0">
            <h1 className="text-3xl md:text-4xl font-secondary text-bold mb-8 text-primary-studio">
              {studioStore.on_sale_page.title}
            </h1>
            <DynamicProductsPage
              initialProducts={initialProducts}
              crossSellProducts={initialCrossSellProducts}
              getProductsKeyFn={getOnSaleProductsKey}
              onSaleOnly
            />
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}));

OnSalePage.propTypes = {
  initialProducts: PropTypes.array.isRequired,
  initialCrossSellProducts: PropTypes.array.isRequired,
  studioStore: PropTypes.object.isRequired,
  wooCommerceStore: PropTypes.object.isRequired,
};

export async function getStaticProps() {
  const { data } = await axios.get(`${process.env.NEXT_PUBLIC_WORDPRESS_SITE_URL}wp-json/beaver_next_wordpress/v1/settings`);
  const { data: studioData } = await axios.get(`${process.env.NEXT_PUBLIC_WORDPRESS_SITE_URL}wp-json/beaver_next_wordpress/v1/acf_studio`);

  // Fetch initial products with SSR
  const { data: initialProducts, crossSellProducts: initialCrossSellProducts } = await getProductsData(
    10, // perPage
    'publish',
    false,
    '',
    false,
    1,
    [],
    'date',
    'desc',
    false,
    true, // on_sale parameter
  );

  let lang = 'en';

  if (data?.language) {
    lang = data.language.split('_')[0];
  }

  const messages = await import(`../messages/${lang}.json`);

  return {
    props: {
      data: data || {},
      studioData: studioData || {},
      messages: messages.default,
      initialProducts,
      initialCrossSellProducts,
    },
    revalidate: 1, // Revalidate every 5 minutes
  };
}

export default OnSalePage;